import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createLogger } from '../../utils/logger';
import { createScan, listBackgrounds, submitBatch } from '../api';
import { NONE_BACKGROUND, type InteriorBackground, type InteriorScanSettings } from '../types';

const log = createLogger('InteriorGeneratePage');

// Sentinel select value meaning "let the server pick its configured
// default" — distinct from the `'none'` value (skip replacement).
const USE_SERVER_DEFAULT = '';

/**
 * Mirrors lightning's `GeneratePage` layout (Single scan + Batch upload
 * panels side-by-side) but with the trimmed-down interior payload
 * shape: `{scan_id, source?}`. There's no photobooth picker /
 * license-plate option / per-scan settings — the interior pipeline
 * pulls everything it needs from the uvcamp inspection response.
 *
 * No callback-URL input here either: lightning's Generate page doesn't
 * expose one (web hooks are configured server-side via env / settings),
 * and the user-facing flow for both apps is "submit, then watch Live
 * Scans". The backend ``/scans`` and ``/api/batches`` endpoints still
 * accept ``callback_url`` for programmatic / API-key callers.
 */

interface SubmissionResult {
  scan_id: string;
  job_id?: string;
  batch_id?: string;
  status: 'submitted' | 'error';
  error?: string;
}

export function InteriorGeneratePage() {
  // Single-scan state
  const [singleScanId, setSingleScanId] = useState('');
  const [singleSubmitting, setSingleSubmitting] = useState(false);
  const [singleResult, setSingleResult] = useState<SubmissionResult | null>(null);

  // Batch-upload state
  const [batchFileName, setBatchFileName] = useState<string | null>(null);
  const [batchScanIds, setBatchScanIds] = useState<string[]>([]);
  const [batchResult, setBatchResult] = useState<SubmissionResult | null>(null);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Background selection — shared by single + batch submits. Empty
  // string means "use server default"; `'none'` skips replacement.
  const [backgrounds, setBackgrounds] = useState<InteriorBackground[]>([]);
  const [selectedBackground, setSelectedBackground] = useState<string>(USE_SERVER_DEFAULT);

  // "Clean the vehicle" toggle — shared by single + batch submits.
  // Defaults to true (the server default); when unchecked we send
  // `settings.clean_vehicle: false` so the cleaning instruction is
  // dropped from the enhancement prompts.
  const [cleanVehicle, setCleanVehicle] = useState(true);

  // Per-request generation toggles — shared by single + batch submits.
  // Defaults mirror the backend `InteriorScanSettings`.
  const [replaceBackground, setReplaceBackground] = useState(true);
  const [enhanceDynamic360, setEnhanceDynamic360] = useState(false);
  const [enhanceCrops, setEnhanceCrops] = useState(true);
  const [manualReview, setManualReview] = useState(true);

  useEffect(() => {
    listBackgrounds()
      .then(setBackgrounds)
      .catch((err) => log.error(`Failed to load backgrounds: ${err}`));
  }, []);

  // `undefined` -> omit from request (server default); otherwise send
  // the chosen name. The background name is only meaningful when
  // replacement is enabled.
  const backgroundNameForRequest = !replaceBackground
    ? undefined
    : selectedBackground === USE_SERVER_DEFAULT
      ? undefined
      : selectedBackground;

  // Full settings object sent on every web submit so per-request values
  // reliably override the server config.
  const settingsForRequest: InteriorScanSettings = useMemo(
    () => ({
      clean_vehicle: cleanVehicle,
      replace_background: replaceBackground,
      enhance_dynamic_360: enhanceDynamic360,
      enhance_crops: enhanceCrops,
      manual_review: manualReview,
    }),
    [cleanVehicle, replaceBackground, enhanceDynamic360, enhanceCrops, manualReview],
  );

  const renderToggle = (
    idPrefix: string,
    key: string,
    checked: boolean,
    onChange: (value: boolean) => void,
    label: string,
    description: string,
  ) => (
    <div>
      <label htmlFor={`${idPrefix}-${key}`} className="flex items-center gap-3 cursor-pointer">
        <input
          id={`${idPrefix}-${key}`}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </label>
      <p className="text-xs text-slate-400 mt-1 ml-7">{description}</p>
    </div>
  );

  const renderBackgroundPicker = (idPrefix: string) => (
    <div className="space-y-2">
      {renderToggle(
        idPrefix,
        'replace-background',
        replaceBackground,
        setReplaceBackground,
        'Replace background',
        'Composite a managed background behind the interior. Uncheck to leave the original image background untouched.',
      )}
      <div>
        <label
          htmlFor={`${idPrefix}-background`}
          className="block text-sm font-medium text-slate-700 mb-2"
        >
          Background
        </label>
        <select
          id={`${idPrefix}-background`}
          value={selectedBackground}
          onChange={(e) => setSelectedBackground(e.target.value)}
          disabled={!replaceBackground}
          className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value={USE_SERVER_DEFAULT}>Server default</option>
          {backgrounds
            .filter((b) => b.name !== NONE_BACKGROUND)
            .map((b) => (
              <option key={b.name} value={b.name}>
                {b.name}
                {b.is_default ? ' (default)' : ''}
              </option>
            ))}
        </select>
      </div>
    </div>
  );

  const renderGenerationToggles = (idPrefix: string) => (
    <div className="space-y-3">
      {renderToggle(
        idPrefix,
        'clean-vehicle',
        cleanVehicle,
        setCleanVehicle,
        'Clean vehicle interior',
        "Remove external objects from the car's interior. Uncheck to leave loose objects in place.",
      )}
      {renderToggle(
        idPrefix,
        'enhance-dynamic-360',
        enhanceDynamic360,
        setEnhanceDynamic360,
        'Enhance dynamic 360',
        'Run AI enhancement on the full 360 panorama image.',
      )}
      {renderToggle(
        idPrefix,
        'enhance-crops',
        enhanceCrops,
        setEnhanceCrops,
        'Enhance crops',
        'Run AI enhancement on the per-scene crops.',
      )}
      {renderToggle(
        idPrefix,
        'manual-review',
        manualReview,
        setManualReview,
        'Manual review',
        'Hold the generation for human review before publishing instead of delivering automatically.',
      )}
    </div>
  );

  // ----- Single scan ------------------------------------------------------

  const submitSingleScan = useCallback(async () => {
    const scanId = singleScanId.trim();
    if (!scanId) return;

    setSingleSubmitting(true);
    setSingleResult(null);
    try {
      const data = await createScan(scanId, {
        source: 'manual',
        backgroundName: backgroundNameForRequest,
        settings: settingsForRequest,
      });
      log.info(`Interior scan submitted: scan_id=${scanId}, job_id=${data.job_id}`);
      setSingleResult({ scan_id: scanId, job_id: data.job_id, status: 'submitted' });
      setSingleScanId('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      log.error(`Interior scan submission failed: ${scanId}: ${message}`);
      setSingleResult({ scan_id: scanId, status: 'error', error: message });
    } finally {
      setSingleSubmitting(false);
    }
  }, [singleScanId, backgroundNameForRequest, settingsForRequest]);

  // ----- Batch upload -----------------------------------------------------

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBatchFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;
      const lines = text.split(/[\r\n]+/).filter((line) => line.trim());
      const scanIds: string[] = [];
      for (const line of lines) {
        const firstColumn = line.split(',')[0]?.trim();
        if (firstColumn) scanIds.push(firstColumn);
      }
      const validIds = scanIds.filter((id) => {
        const lower = id.toLowerCase();
        if (
          ['scan_id', 'scanid', 'id', 'scan', 'inspection_id', 'inspectionid'].includes(lower)
        ) {
          return false;
        }
        return /^[a-zA-Z0-9._-]+$/.test(id) && id.length > 3;
      });
      setBatchScanIds(validIds);
      setBatchResult(null);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const submitBatchScans = useCallback(async () => {
    if (batchScanIds.length === 0 || !batchFileName) return;
    setBatchSubmitting(true);
    setBatchResult(null);
    try {
      const data = await submitBatch(batchFileName, batchScanIds, {
        backgroundName: backgroundNameForRequest,
        settings: settingsForRequest,
      });
      log.info(`Interior batch submitted: batch_id=${data.batch_id}, scans=${data.total_scans}`);
      setBatchResult({
        scan_id: batchFileName,
        batch_id: data.batch_id,
        status: 'submitted',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      log.error(`Interior batch submission failed: ${message}`);
      setBatchResult({ scan_id: batchFileName, status: 'error', error: message });
    } finally {
      setBatchSubmitting(false);
    }
  }, [batchScanIds, batchFileName, backgroundNameForRequest, settingsForRequest]);

  const clearBatch = () => {
    setBatchFileName(null);
    setBatchScanIds([]);
    setBatchResult(null);
  };

  // ----- Render -----------------------------------------------------------

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* ----- Single scan ----- */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Single Scan</h2>
            <p className="text-sm text-slate-500">Crop and enhance the interior for one scan ID</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="interior-scanId" className="block text-sm font-medium text-slate-700 mb-2">
              Scan ID
            </label>
            <input
              id="interior-scanId"
              type="text"
              value={singleScanId}
              onChange={(e) => setSingleScanId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSingleScan()}
              placeholder="e.g., 57942aad-a26d-4b05-8c1b-7de5aa32326d"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 font-mono text-sm transition-all duration-200"
            />
          </div>

          {renderBackgroundPicker('single')}

          {renderGenerationToggles('single')}

          <button
            onClick={submitSingleScan}
            disabled={!singleScanId.trim() || singleSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {singleSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate
              </>
            )}
          </button>

          {singleResult && (
            <div
              className={`p-4 rounded-lg ${
                singleResult.status === 'submitted' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {singleResult.status === 'submitted' ? (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Scan submitted successfully</p>
                    <p className="text-xs font-mono mt-1 text-slate-600">Job ID: {singleResult.job_id}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Failed to submit scan</p>
                    <p className="text-xs text-slate-600 mt-1">{singleResult.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ----- Batch upload ----- */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Batch Upload</h2>
            <p className="text-sm text-slate-500">Submit a CSV of scan IDs</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all duration-200">
              <svg className="w-12 h-12 mx-auto text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-slate-700">
                Drop a CSV file here or <span className="text-indigo-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">One scan ID per line — the first column is used</p>
            </div>
          </div>

          {batchScanIds.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    <span className="text-slate-900 font-semibold">{batchScanIds.length}</span> scan IDs loaded
                  </span>
                  {batchFileName && (
                    <span className="text-xs text-slate-400 font-mono">({batchFileName})</span>
                  )}
                </div>
                <button
                  onClick={clearBatch}
                  className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {batchScanIds.slice(0, 10).map((id, idx) => (
                  <div key={idx} className="text-xs font-mono text-slate-500 truncate">
                    {id}
                  </div>
                ))}
                {batchScanIds.length > 10 && (
                  <div className="text-xs text-slate-400">… and {batchScanIds.length - 10} more</div>
                )}
              </div>
            </div>
          )}

          {renderBackgroundPicker('batch')}

          {renderGenerationToggles('batch')}

          <button
            onClick={submitBatchScans}
            disabled={batchScanIds.length === 0 || batchSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {batchSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Submit Batch ({batchScanIds.length} scans)
              </>
            )}
          </button>

          {batchResult && (
            <div
              className={`p-4 rounded-lg ${
                batchResult.status === 'submitted' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {batchResult.status === 'submitted' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium">Batch submitted successfully</p>
                      <p className="text-xs font-mono mt-1 text-slate-600">Batch ID: {batchResult.batch_id}</p>
                    </div>
                  </div>
                  <Link
                    to="/interior/live"
                    className="block w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-center text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    View Progress in Live Scans
                  </Link>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Failed to submit batch</p>
                    <p className="text-xs text-slate-600 mt-1">{batchResult.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
